<template>
  <div class="country-report-container">
    <div v-if="!reportData" class="loading">Loading...</div>

    <div v-if="reportData" class="report-content"  ref="reportContainer">
      <!-- 标题区域 -->
      <div class="header-section">
        <div class="header-top">
          <h1 class="report-title">
            {{ reportData.country_name }} Country Analysis Report
          </h1>
          <div class="header-actions">
            <customize-button
              type="primary"
              @click="generatePDF"
              :loading="isDownloading"
            >
              Generate PDF
            </customize-button>
            <div class="status-badge">ANALYSIS COMPLETED</div>
          </div>
        </div>

        <div class="report-stats">
          <div class="stat-item">
            <span class="stat-label">Processing Statistics:</span>
            <span class="stat-value">
              Successfully processed
              {{ reportData.analysis_result?.successful_reports }} reports,
              failed {{ reportData.analysis_result?.failed_reports }} reports
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Processing Time:</span>
            <span class="stat-value">
              {{ reportData.analysis_result?.processing_time }} seconds
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Analysis Time:</span>
            <span class="stat-value">
              {{ formatDate(reportData.analysis_result.analysis_date) }}
            </span>
          </div>
        </div>
      </div>

      <!-- 评级信息 -->
      <div class="rating-section">
        <h2 class="section-title">🏆 Each rating agency</h2>

        <div
          class="rating-cards"
          v-if="reportData.analysis_result?.reports_processed"
        >
          <div
            v-for="(report, index) in reportData.analysis_result
              .reports_processed"
            :key="index"
            class="rating-card"
          >
            <div class="agency-name">
              {{ report.rating_agency || 'Unknown organization' }}
            </div>
            <div class="rating-value" :class="getRatingClass(report.outlook)">
              {{ formatRating(report.long_term_rating, report.outlook) }}
            </div>
            <div class="file-name">{{ report.file_name }}</div>
          </div>
        </div>

        <!-- 无数据显示 -->
        <div v-else class="no-data">No rating agency data available.</div>
      </div>

      <!-- 各机构详细分析 -->
      <!-- 各机构详细分析 -->
      <div class="analysis-section">
        <h2 class="section-title">📊 Detailed analysis of each institution</h2>

        <!-- 机构详细分析卡片 -->
        <div
          class="analysis-cards"
          v-if="reportData.analysis_result?.reports_processed"
        >
          <div
            v-for="(report, index) in reportData.analysis_result
              .reports_processed"
            :key="index"
            class="analysis-card"
          >
            <!-- 卡片头部 -->
            <div class="analysis-header">
              <div class="agency-info">
                <h3>{{ report.rating_agency }} - {{ truncateFileName(report.file_name, 75) }}</h3>
              </div>
              <div class="rating-info">
                <div class="rating-badge">
                  Rating: {{ report.long_term_rating }}
                </div>
                <div class="outlook-badge">Outlook: {{ report.outlook }}</div>
                <div class="date-info">
                  Date: {{ formatReportDate(report.report_date) }}
                </div>
              </div>
            </div>

            <!-- 关键指标区域 -->
            <div class="metrics-area">
              <h4 class="metrics-title">🔍 Key Performance Indicators</h4>
              <div class="metrics-split">
                <!-- 实际值区域 -->
                <div class="metrics-column actual-column">
                  <h5 class="column-title">Actual Values</h5>
                  <div class="metrics-list">
                    <template v-for="(value, key) in report.key_metrics_actual">
                      <div
                        :key="`${key}_actual`"
                        class="metric-item"
                        v-tooltip="getMetricContext(report, key, 'actual')"
                      >
                        {{ formatKeyName(key) }}: {{ value }}
                        <span v-if="getMetricContext(report, key, 'actual')" class="context-indicator">ⓘ</span>
                      </div>
                    </template>
                    <!-- 如果没有实际值数据，显示空状态 -->
                    <div
                      v-if="!report.key_metrics_actual || Object.keys(report.key_metrics_actual).length === 0"
                      class="no-metrics"
                    >
                      No Actual Key Performance Indicators data available.
                    </div>
                  </div>
                </div>

                <!-- 预测值区域 -->
                <div class="metrics-column forecast-column">
                  <h5 class="column-title">Forecast Values</h5>
                  <div class="metrics-list">
                    <template v-for="(value, key) in report.key_metrics_forecast">
                      <div
                        :key="`${key}_forecast`"
                        class="metric-item"
                        v-tooltip="getMetricContext(report, key, 'forecast')"
                      >
                        {{ formatKeyName(key) }}: {{ value }}
                        <span v-if="getMetricContext(report, key, 'forecast')" class="context-indicator">ⓘ</span>
                      </div>
                    </template>
                    <!-- 如果没有预测值数据，显示空状态 -->
                    <div
                      v-if="!report.key_metrics_forecast || Object.keys(report.key_metrics_forecast).length === 0"
                      class="no-metrics"
                    >
                      No Forecast Key Performance Indicators data available.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 亮点和关注点区域 -->
            <div class="highlights-lowlights">
              <!-- 亮点区域 -->
              <div class="highlights-area">
                <h4 class="highlights-title">
                  ✅ Highlights ({{ getHighlightsCount(report.highlights) }})
                </h4>
                <ul class="highlights-list">
                  <li
                    v-for="(highlight, hIndex) in parseArrayString(
                      report.highlights,
                    )"
                    :key="hIndex"
                  >
                    {{ highlight }}
                  </li>
                </ul>
                <div
                  v-if="
                    !report.highlights ||
                    parseArrayString(report.highlights).length === 0
                  "
                  class="no-highlights"
                >
                  No highlight data available.
                </div>
              </div>

              <!-- 关注点区域 -->
              <div class="lowlights-area">
                <h4 class="lowlights-title">
                  ⚠️ Attention ({{ getLowlightsCount(report.lowlights) }})
                </h4>
                <ul class="lowlights-list">
                  <li
                    v-for="(lowlight, lIndex) in parseArrayString(
                      report.lowlights,
                    )"
                    :key="lIndex"
                  >
                    {{ lowlight }}
                  </li>
                </ul>
                <div
                  v-if="
                    !report.lowlights ||
                    parseArrayString(report.lowlights).length === 0
                  "
                  class="no-lowlights"
                >
                  暂无关注点数据
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 汇总分析结果 -->
      <div class="summary-section">
        <h2 class="section-title">❤️ Summary of analysis results</h2>

        <div class="summary-content">
          <!-- 各评级机构汇总 -->
          <!-- <div class="agencies-summary">
            <h3 class="subsection-title">Summary of each rating agency</h3>

            <div class="agencies-grid">
              <div class="agency-summary-card">
                <div class="agency-header">
                  <span class="agency-name">Moody's</span>
                </div>
                <div class="rating-display negative">
                  {{
                    reportData?.analysis_result?.consolidated_analysis
                      ?.rating_comparison?.["Moody's"]
                  }}
                  (Negative)
                </div>
              </div>

              <div class="agency-summary-card">
                <div class="agency-header">
                  <span class="agency-name">Fitch Ratings</span>
                </div>
                <div class="rating-display stable">
                  {{
                    reportData?.analysis_result?.consolidated_analysis
                      ?.rating_comparison?.['Fitch']
                  }}
                  (Stable)
                </div>
              </div>
            </div>
          </div> -->

          <!-- 汇总亮点和关注点 -->
          <div class="summary-highlights-lowlights">
            <!-- 汇总亮点 -->
            <div class="summary-highlights">
              <div class="summary-title">
                <span class="icon">✅</span>
                <span class="title-text">
                  Summary of Highlights ({{ getConsolidatedHighlightsCount() }})
                </span>
              </div>
              <ul class="summary-list">
                <li
                  v-for="(highlight, index) in parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.consolidated_highlights,
                  )"
                  :key="index"
                >
                  {{ highlight }}
                </li>
              </ul>
              <div
                v-if="
                  !reportData?.analysis_result?.consolidated_analysis
                    ?.consolidated_highlights ||
                  parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.consolidated_highlights,
                  ).length === 0
                "
                class="no-summary-data"
              >
                No summary highlight data available.
              </div>
            </div>

            <!-- 汇总关注点 -->
            <div class="summary-lowlights">
              <div class="summary-title">
                <span class="icon">⚠️</span>
                <span class="title-text">
                  Summary of Key Points ({{ getConsolidatedLowlightsCount() }})
                </span>
              </div>
              <ul class="summary-list">
                <li
                  v-for="(lowlight, index) in parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.consolidated_lowlights,
                  )"
                  :key="index"
                >
                  {{ lowlight }}
                </li>
              </ul>
              <div
                v-if="
                  !reportData?.analysis_result?.consolidated_analysis
                    ?.consolidated_lowlights ||
                  parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.consolidated_lowlights,
                  ).length === 0
                "
                class="no-summary-data"
              >
                No summary of focus point data available.
              </div>
            </div>
          </div>

          <!-- Common Strengths 和 Common Weaknesses -->
          <div class="common-analysis">
            <!-- Common Strengths -->
            <div class="common-strengths">
              <div class="common-title">
                <span class="icon">💪</span>
                <span class="title-text">
                  COMMON STRENGTH ({{ getCommonStrengthsCount() }})
                </span>
              </div>
              <ul class="common-list">
                <li
                  v-for="(strength, index) in parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_strengths,
                  )"
                  :key="index"
                >
                  {{ strength }}
                </li>
              </ul>
              <div
                v-if="
                  !reportData?.analysis_result?.consolidated_analysis
                    ?.common_strengths ||
                  parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_strengths,
                  ).length === 0
                "
                class="no-common-data"
              >
                暂无Common Strength数据
              </div>
            </div>

            <!-- Common Weaknesses -->
            <div class="common-weaknesses">
              <div class="common-title">
                <span class="icon">⚡</span>
                <span class="title-text">
                  COMMON WEAKNESSES ({{ getCommonWeaknessesCount() }})
                </span>
              </div>
              <ul class="common-list">
                <li
                  v-for="(weakness, index) in parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_weaknesses,
                  )"
                  :key="index"
                >
                  {{ weakness }}
                </li>
              </ul>
              <div
                v-if="
                  !reportData?.analysis_result?.consolidated_analysis
                    ?.common_weaknesses ||
                  parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_weaknesses,
                  ).length === 0
                "
                class="no-common-data"
              >
                暂无Common Weaknesses数据
              </div>
            </div>
          </div>

          <!-- Common Risks -->
          <div class="common-risks-section">
            <div class="common-risks">
              <div class="common-risks-title">
                <span class="icon">⚠️</span>
                <span class="title-text">
                  COMMON RISK ({{ getCommonRisksCount() }})
                </span>
              </div>
              <ul class="common-risks-list">
                <li
                  v-for="(risk, index) in parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_risks,
                  )"
                  :key="index"
                >
                  {{ risk }}
                </li>
              </ul>
              <div
                v-if="
                  !reportData?.analysis_result?.consolidated_analysis
                    ?.common_risks ||
                  parseArrayString(
                    reportData?.analysis_result?.consolidated_analysis
                      ?.common_risks,
                  ).length === 0
                "
                class="no-common-risks-data"
              >
                No Common Risk data available.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getResult } from './api'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

export default {
  data() {
    return {
      id: this.$route.params.id,
      reportData: null,
      isDownloading: false,
    }
  },
  created() {
    this.fetchReportData()
  },
  directives: {
    tooltip: {
      bind(el, binding) {
        el.addEventListener('mouseenter', () => {
          if (!binding.value) return;

          // Create tooltip element
          const tooltip = document.createElement('div');
          tooltip.className = 'custom-tooltip';
          tooltip.innerHTML = `<div class="tooltip-context">${binding.value.context}</div>`;

          // Style the tooltip
          Object.assign(tooltip.style, {
            position: 'absolute',
            background: '#fef3c7', // Light yellow background
            color: '#92400e', // Darker yellow/brown text
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: '1000',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            maxWidth: '300px',
            wordWrap: 'break-word',
            pointerEvents: 'none',
            border: '1px solid #fde68a' // Light yellow border
          });

          document.body.appendChild(tooltip);
          el._tooltip = tooltip;

          // Position tooltip
          const rect = el.getBoundingClientRect();
          tooltip.style.left = rect.left + 'px';
          tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
        });

        el.addEventListener('mouseleave', () => {
          if (el._tooltip) {
            document.body.removeChild(el._tooltip);
            delete el._tooltip;
          }
        });
      },

      unbind(el) {
        if (el._tooltip) {
          document.body.removeChild(el._tooltip);
          delete el._tooltip;
        }
      }
    }
  },
  methods: {
    generatePDF() {
      this.isDownloading = true
      let ref = this.$refs.reportContainer
      const fileName = `${this.reportData.country_name || 'Country'}_Report.pdf`

      html2canvas(ref, {
        backgroundColor: '#f8fafc',
        scale: 2,
        useCORS: true,
        logging: false,
      })
        .then(canvas => {
          // canvas宽度
          let contentWidth = canvas.width
          // canvas高度
          let contentHeight = canvas.height
          // 一页pdf html页面生成的canvas高度
          let pageHeight = (contentWidth / 592.28) * 841.89
          let leftHeight = contentHeight
          let position = 0
          // a4纸的尺寸[592.28,841.89]，html页面生成的canvas在pdf中图片的宽高
          let imgWidth = 595.28
          let imgHeight = (592.28 / contentWidth) * contentHeight
          let pageData = canvas.toDataURL('image/jpeg', 1.0)
          let PDF = new JsPDF('', 'pt', 'a4')

          // 当内容未超过pdf一页显示的范围，无需分页
          if (leftHeight < pageHeight) {
            PDF.addImage(pageData, 'JPEG', 0, 0, imgWidth, imgHeight)
          } else {
            while (leftHeight > 0) {
              PDF.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight)
              leftHeight -= pageHeight
              position -= 841.89
              // 避免添加空白页
              if (leftHeight > 0) {
                PDF.addPage()
              }
            }
          }

          PDF.save(fileName)
          this.isDownloading = false

          this.$message({
            message: 'PDF generated successfully',
            type: 'success',
          })
        })
        .catch(() => {
          this.isDownloading = false
          this.$message({
            message: 'Images generated failure',
            type: 'error',
          })
        })
    },
    async fetchReportData() {
      try {
        const response = await getResult({ id: this.id })
        console.log('Raw response:', response)

        // response本身就是字符串，直接解析
        this.reportData = JSON.parse(response)

        // console.log('Parsed data:', this.reportData)
      } catch (error) {
        console.error('Error:', error)
        this.reportData = { error: 'Failed to load data' }
      }
    },
    truncateFileName(fileName, maxLength) {
      if (!fileName) return '';
      if (fileName.length <= maxLength) return fileName;
      return fileName.substring(0, maxLength) + '...';
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },
    getMetricContext(report, key, type = null) {
      let contextData;

      // 根据类型选择不同的上下文字段
      if (type === 'actual') {
        contextData = report.key_metrics_with_context_actual?.[key];
      } else if (type === 'forecast') {
        contextData = report.key_metrics_with_context_forecast?.[key];
      } else {
        return null;
      }

      // 处理上下文数据
      if (contextData) {
        if (Array.isArray(contextData) && contextData.length > 0) {
          return {
            context: contextData[0].context || 'No context available'
          };
        }
        // 如果是对象，直接使用其 context 字段
        else if (typeof contextData === 'object' && contextData.context) {
          return {
            context: contextData.context || 'No context available'
          };
        }
      }

      return null;
    },
    formatRating(longTermRating, outlook) {
      if (longTermRating && outlook) {
        return `${longTermRating} (${outlook})`
      } else if (longTermRating) {
        return longTermRating
      } else {
        return '暂无评级'
      }
    },
    getRatingClass(outlook) {
      if (!outlook) return 'neutral'

      const outlookLower = outlook.toLowerCase()
      if (outlookLower.includes('negative')) return 'negative'
      if (outlookLower.includes('stable')) return 'stable'
      if (outlookLower.includes('positive')) return 'positive'

      return 'neutral'
    },
    formatReportDate(dateString) {
      if (!dateString) return '05 Dec 2023'
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    },
    // 解析数组字符串
    parseArrayString(arrayString) {
      if (!arrayString) return []

      try {
        // 如果已经是数组，直接返回
        if (Array.isArray(arrayString)) {
          return arrayString
        }

        // 如果是字符串，尝试解析为JSON
        if (typeof arrayString === 'string') {
          return JSON.parse(arrayString)
        }

        return []
      } catch (error) {
        console.error('Error parsing array string:', error, arrayString)
        return []
      }
    },
    // 获取亮点数量
    getHighlightsCount(highlights) {
      const parsed = this.parseArrayString(highlights)
      return parsed.length
    },
    // 获取关注点数量
    getLowlightsCount(lowlights) {
      const parsed = this.parseArrayString(lowlights)
      return parsed.length
    },
    // 获取汇总亮点数量
    getConsolidatedHighlightsCount() {
      const highlights =
        this.reportData?.analysis_result?.consolidated_analysis
          ?.consolidated_highlights
      const parsed = this.parseArrayString(highlights)
      return parsed.length
    },
    // 获取汇总关注点数量
    getConsolidatedLowlightsCount() {
      const lowlights =
        this.reportData?.analysis_result?.consolidated_analysis
          ?.consolidated_lowlights
      const parsed = this.parseArrayString(lowlights)
      return parsed.length
    },
    // 获取Common Strengths数量
    getCommonStrengthsCount() {
      const strengths =
        this.reportData?.analysis_result?.consolidated_analysis
          ?.common_strengths
      const parsed = this.parseArrayString(strengths)
      return parsed.length
    },
    // 获取Common Weaknesses数量
    getCommonWeaknessesCount() {
      const weaknesses =
        this.reportData?.analysis_result?.consolidated_analysis
          ?.common_weaknesses
      const parsed = this.parseArrayString(weaknesses)
      return parsed.length
    },
    // 获取Common Risks数量
    getCommonRisksCount() {
      const risks =
        this.reportData?.analysis_result?.consolidated_analysis?.common_risks
      const parsed = this.parseArrayString(risks)
      return parsed.length
    },
    // 格式化关键指标的 key 名称
    formatKeyName(key) {
      if (!key) return ''

      // 将下划线替换为空格，并将每个单词首字母大写
      return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
    // 判断值是否为对象（用于多年份数据）
    isObjectValue(value) {
      return (
        typeof value === 'object' && !Array.isArray(value) && value !== null
      )
    },
    // 格式化关键指标的 value 值（保留用于简单值的处理）
    formatKeyValue(value) {
      if (!value) return ''

      // 如果是数组
      if (Array.isArray(value)) {
        return value.join(', ')
      }

      // 如果是包含 [Image #1] 格式的字符串，直接返回
      if (typeof value === 'string' && value.includes('[Image #')) {
        return value
      }

      return value
    },
  },
}
</script>

<style scoped>
.country-report-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.loading {
  text-align: center;
  padding: 60px;
  font-size: 18px;
  color: #64748b;
}
.custom-tooltip {
  background: #fef3c7 !important; /* Light yellow background */
  color: #92400e !important; /* Darker yellow/brown text for contrast */
  padding: 8px 12px !important;
  border-radius: 4px !important;
  font-size: 12px !important;
  z-index: 1000 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
  max-width: 300px !important;
  word-wrap: break-word !important;
  pointer-events: none !important;
  border: 1px solid #fde68a !important; /* Light yellow border */
}
.tooltip-context {
  color: #e5e7eb;
}
.report-content {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}
/* Add these styles to the CSS section */
.metric-item {
  background: #f8fafc;
  border-left: 4px solid #3b82f6;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e2e8f0;
  position: relative;
  cursor: pointer;
}
.metrics-split {
  display: flex;
  gap: 24px;
}

.metrics-column {
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.actual-column {
  background: #f0fdf4; /* 绿色背景表示实际值 */
  border-left: 4px solid #10b981;
}

.forecast-column {
  background: #fffbeb; /* 黄色背景表示预测值 */
  border-left: 4px solid #f59e0b;
}

.column-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #374151;
}
.context-indicator {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}
/* 标题区域 */
.header-section {
  background: linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%);
  padding: 32px;
  color: #0f172a;
  border-bottom: 1px solid #e2e8f0;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.country-flag {
  font-size: 32px;
}

.report-title {
  font-size: 28px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  flex: 1;
}

.status-badge {
  background: #10b981;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
}

.report-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-label {
  font-weight: 600;
  color: #64748b;
  min-width: 150px;
}

.stat-value {
  color: #1e293b;
  font-weight: 500;
}

/* 评级信息 */
.rating-section {
  padding: 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.section-title {
  color: #f59e0b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 24px 0;
}

.rating-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.rating-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.agency-name {
  color: #374151;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
}

.rating-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.rating-value.negative {
  color: #ef4444;
}

.rating-value.stable {
  color: #3b82f6;
}

.rating-value.positive {
  color: #10b981;
}

.rating-value.neutral {
  color: #6b7280;
}

.file-name {
  color: #6b7280;
  font-size: 12px;
}

.no-data {
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 40px;
}

/* 各机构详细分析 */
.analysis-section {
  padding: 32px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
}

.analysis-section .section-title {
  color: #7c3aed;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 24px 0;
}

.analysis-cards {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.analysis-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  color: #374151;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 卡片头部 */
.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
}

.agency-info h3 {
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.rating-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.rating-badge {
  background: #3b82f6;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.outlook-badge {
  background: #ef4444;
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.date-info {
  color: #6b7280;
  font-size: 12px;
}

/* 关键指标区域 */
.metrics-area {
  margin-bottom: 24px;
}

.metrics-title {
  color: #7c2d12;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-item {
  background: #f8fafc;
  border-left: 4px solid #3b82f6;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e2e8f0;
}

.no-metrics {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  text-align: center;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

/* 亮点和关注点区域 */
.highlights-lowlights {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.highlights-area {
  background: #f0fdf4;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #10b981;
  border: 1px solid #bbf7d0;
}

.lowlights-area {
  background: #fffbeb;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #f59e0b;
  border: 1px solid #fed7aa;
}

.highlights-title {
  color: #065f46;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.lowlights-title {
  color: #92400e;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.highlights-list,
.lowlights-list {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.highlights-list li,
.lowlights-list li {
  color: #047857;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 8px;
  position: relative;
}

.lowlights-list li {
  color: #a16207;
}

.highlights-list li::before {
  content: '•';
  color: #10b981;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.lowlights-list li::before {
  content: '•';
  color: #f59e0b;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.no-highlights,
.no-lowlights {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

/* 汇总分析结果 */
.summary-section {
  padding: 32px;
  background: #f8fafc;
  color: #1f2937;
  border-radius: 0 0 16px 16px;
  border-top: 1px solid #e2e8f0;
}

.summary-section .section-title {
  color: #f97316;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 24px 0;
  text-align: center;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* 各评级机构汇总 */
.agencies-summary {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.subsection-title {
  color: #10b981;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
}

.agencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.agency-summary-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #d1d5db;
  text-align: center;
}

.agency-header {
  margin-bottom: 12px;
}

.agency-name {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.rating-display {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  padding: 8px;
  border-radius: 6px;
}

.rating-display.negative {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.rating-display.stable {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

/* 汇总亮点和关注点 */
.summary-highlights-lowlights {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.summary-highlights {
  background: #f0fdf4;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #10b981;
  border: 1px solid #bbf7d0;
}

.summary-lowlights {
  background: #fffbeb;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #f59e0b;
  border: 1px solid #fed7aa;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.summary-title .icon {
  font-size: 16px;
}

.summary-title .title-text {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.summary-list {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.summary-highlights .summary-list li {
  color: #047857;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
  position: relative;
}

.summary-lowlights .summary-list li {
  color: #a16207;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
  position: relative;
}

.summary-highlights .summary-list li::before {
  content: '•';
  color: #10b981;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.summary-lowlights .summary-list li::before {
  content: '•';
  color: #f59e0b;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.no-summary-data {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

/* Common Strengths 和 Common Weaknesses */
.common-analysis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.common-strengths {
  background: #ecfdf5;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #22c55e;
  border: 1px solid #bbf7d0;
}

.common-weaknesses {
  background: #fef2f2;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #ef4444;
  border: 1px solid #fecaca;
}

.common-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.common-title .icon {
  font-size: 16px;
}

.common-title .title-text {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.common-list {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.common-strengths .common-list li {
  color: #059669;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
  position: relative;
}

.common-weaknesses .common-list li {
  color: #dc2626;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
  position: relative;
}

.common-strengths .common-list li::before {
  content: '•';
  color: #22c55e;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.common-weaknesses .common-list li::before {
  content: '•';
  color: #ef4444;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.no-common-data {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

/* Common Risks */
.common-risks-section {
  width: 100%;
}

.common-risks {
  background: #fef3c7;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #f59e0b;
  border: 1px solid #fde68a;
}

.common-risks-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.common-risks-title .icon {
  font-size: 16px;
}

.common-risks-title .title-text {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.common-risks-list {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.common-risks-list li {
  color: #b45309;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
  position: relative;
}

.common-risks-list li::before {
  content: '•';
  color: #f59e0b;
  font-weight: bold;
  position: absolute;
  left: -12px;
}

.no-common-risks-data {
  color: #9ca3af;
  font-style: italic;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .summary-highlights-lowlights {
    grid-template-columns: 1fr;
  }

  .common-analysis {
    grid-template-columns: 1fr;
  }

  .agencies-grid {
    grid-template-columns: 1fr;
  }
}
</style>
